import axios, { AxiosResponse } from 'axios';
import * as jose from 'jose';
import queryString from 'query-string';
import { ENV } from '@/configs/env.config';
import { Auth } from '../auth.service';
import { logger } from '../logger.service';
import { slgMockData } from './mock';
import { openNotification } from '../utils/open-notification.util';
import { IAuthData } from '@/shared/interfaces/auth-data.interface';
import { IAgentApi, IAgentsApi } from './interfaces/agent.interface';
import { IVoicemailsResponse } from './interfaces/voicemail.interface';
import { BusEvent, EBusEvent } from '../bus-event/bus-event.service';
import {
  apiInterceptor,
  hubInterceptor,
  apiResponseInterceptor,
  hubResponseInterceptor,
} from './interceptor';
import { IUserInfoApi } from './interfaces/user-info.interface';
import { ISipCredentialsApi } from './interfaces/sip-credentials';
import { EChannelProvider } from './enums/channel-provider.enum';
import { IChats } from './interfaces/chats.interface';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { IChatMessage } from '@/shared/interfaces/chat-message.interface';
import { ITranscriptAnalyticsApi } from './interfaces/transcript-analytics.interface';
import { WrapUpApi } from './interfaces/wrap-up.interface';
import { ISolgariPresenceApi } from './interfaces/presence.interface';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import { SipJs } from '../sip-js.service';
import { TenantInfo } from './interfaces/tenant-info';
import { IMissedCall } from '@/shared/interfaces/missed-call.interface';
import { IClid, IOutboundClid } from './interfaces/outbound-clid.interface';
import { FeatureFlags } from '@/services/solgari-api/interfaces/feature-flags.interface';
import {
  Cdr,
  CdrModel,
  GetCdrListResponse,
} from '@/shared/interfaces/missed-call-cdr.interface';
import {
  mapMissedCallCdr,
  deduplicateCdrsByInterval,
} from '@/shared/mappers/missed-calls.mapper';

const proxy = axios.create();
const hub = axios.create();

proxy.interceptors.request.use(apiInterceptor.success, apiInterceptor.error);
proxy.interceptors.response.use(
  apiResponseInterceptor.success,
  apiResponseInterceptor.error,
);
hub.interceptors.request.use(hubInterceptor.success, hubInterceptor.error);
hub.interceptors.response.use(
  hubResponseInterceptor.success,
  hubResponseInterceptor.error,
);

export enum EParticipantOperations {
  MUTE_UNMUTE = 'MUTE/UNMUTE',
  INVITE = 'INVITE',
  DELETE = 'DELETE',
}

export class SolgariApiService {
  private static readonly solgariHub = ENV.SOLGARI_HUB;

  public static signalR: HubConnection;
  public static forceClose: boolean;

  public static async getAuthData(): Promise<IAuthData> {
    try {
      const [sipCreds, userInfo] = await Promise.all([
        SolgariApiService.getSipCredentials(),
        SolgariApiService.getUserInfo(),
      ]);

      const res = {
        uri: `sip:${sipCreds.extension}@${sipCreds.domain}`,
        password: sipCreds.password,
        extensionId: Number(sipCreds.extension),
        server: sipCreds.wssServers,
        tenantId: userInfo.tenantId,
        tenantDomain: sipCreds.domain,
      } as IAuthData;

      return res;
    } catch (error: any) {
      BusEvent.sendEvent({ event: EBusEvent.SIP_LOGIN_FAILED });

      if (error.response) {
        const text = error.response?.data;

        openNotification({
          onClick: () => window.location?.reload(),
          onClose: undefined,
          buttonText: 'Reload page',
          type: 'error',
          title: text,
          text: 'Please contact the support@gmail.com',
        });
      }
      logger.critical('[Api] Unable to get sip credentials:', error);
    }
  }

  public static async getWrapUpForQueue(queueName: string): Promise<WrapUpApi> {
    try {
      const domain = SipJs.authData.tenantDomain;
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/queues`;
      const result = await hub.get<WrapUpApi[]>(uri);
      const queuesData = result.data;
      if (queueName && queuesData) {
        const queue = queuesData.find((_) => _.name == queueName.slice(1, -1));
        if (queue?.wrapUp != 0) {
          return queue;
        }
      }
    } catch (error) {
      logger.error('[Api] Unable to get wrap up for queue:', error);
    }
  }

  public static async getUserInfo(): Promise<IUserInfoApi> {
    try {
      const urlInstance = new URL(
        `${SolgariApiService.solgariHub}/api/v1/public/users/me`,
      );
      const uri = urlInstance.toString();

      const result = await hub.get<IUserInfoApi>(uri);

      return result.data;
    } catch (error) {
      logger.critical('[Api] Unable to get user info:', error);
    }
  }

  public static async getSipCredentials(): Promise<ISipCredentialsApi> {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/users/me/getsipcredentials`;

      if (ENV.MOCK_SLG_API) {
        return Promise.resolve(slgMockData.getSipCreds);
      }

      const result = await hub.get<ISipCredentialsApi>(uri);
      return result.data;
    } catch (error) {
      logger.critical('[Api] Error getting sip credentials:', error);
    }
  }

  public static async getListOfAgents(): Promise<IAgentApi[]> {
    const limit = 250;
    try {
      const headers = { Accept: 'text/plain' };
      const baseUrl = `${SolgariApiService.solgariHub}/api/v1/public/users/getmy?`;

      const initialUri = `${baseUrl}${queryString.stringify({ Limit: limit, Offset: 0 })}`;

      const {
        data: { count, users: initialUsers },
      } = await hub.get<IAgentsApi>(initialUri, { headers });

      const firstBatch: IAgentApi[] = (initialUsers ?? []).map((agent) => ({
        ...agent,
        fullName: `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim(),
      }));

      if (firstBatch.length >= count) {
        return firstBatch;
      }

      const remainingRecords: number = count - firstBatch.length;
      const additionalRequests: number = Math.ceil(remainingRecords / limit);

      const remainedPromises = Array.from(
        { length: additionalRequests },
        (_, index: number) => {
          const offset: number = (index + 1) * limit;
          const uri = `${baseUrl}${queryString.stringify({ Limit: limit, Offset: offset })}`;

          return hub.get<IAgentsApi>(uri, { headers });
        },
      );

      const remainedBatches = await Promise.all(remainedPromises);

      const allAgents = [
        ...firstBatch,
        ...remainedBatches.flatMap((response: AxiosResponse<IAgentsApi>) =>
          (response.data.users ?? []).map((agent) => ({
            ...agent,
            fullName: `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim(),
          })),
        ),
      ];

      return allAgents;
    } catch (error) {
      logger.error('[Api] Failed to get list of agents: ', error);
      return [];
    }
  }

  public static async getChats(): Promise<IChats> {
    try {
      const email = SolgariApiService.getOwnEmail();
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/chat/${email}/search?Limit=100&Offset=0`;
      const result = await hub.get(uri);
      return result.data;
    } catch (error) {
      logger.error('[Api] Unable to get chats', error);

      return {
        chatsCount: 0,
        chats: [],
      };
    }
  }

  public static async getChatDetails(chatId: string): Promise<IChatMessage[]> {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v2/public/chat/${chatId}/messages`;
      const result = await hub.get(uri);

      return result.data;
    } catch (error) {
      logger.error('[Api] Unable to get chat details', error);
    }
  }

  public static async getMissesCalls(): Promise<IMissedCall[]> {
    const userExtension: string = String(SipJs.authData?.extensionId || '');
    const VoicemailExtension: string | null = '666';

    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/cdr/list/users/me`;
      const params = {
        RecordsPerPage: 1000,
        CallStatus: 'NOANSWER',
        CallDirections: 'Inbound',
        includeDirectMissedCalls: true,
      };
      const response = await hub.get<GetCdrListResponse>(uri, { params });

      const cdrs: CdrModel[] = response.data.data
        ? response.data.data
            .map((cdr: Cdr) =>
              mapMissedCallCdr(cdr, userExtension, VoicemailExtension),
            )
            .filter((cdr: CdrModel | null): cdr is CdrModel => cdr !== null)
        : [];

      const filteredCdrs: CdrModel[] = deduplicateCdrsByInterval(cdrs);

      const mappedMissedCalls = filteredCdrs.map(
        ({
          caller,
          sessionId,
          ringTimeDate,
        }: {
          caller: string | null;
          sessionId: string | null;
          ringTimeDate: string;
        }) => ({
          id: sessionId,
          fullName: 'Missed Call',
          date: new Date(ringTimeDate),
          direction: 'noanswer' as const,
          callStatus: 'NOANSWER',
          durationFormatted: '0',
          phoneNumber: caller,
        }),
      );

      return mappedMissedCalls;
    } catch (error) {
      logger.error('[Api] Unable to get missed calls', error);
      return [];
    }
  }

  public static async searchChatsByIdentity(
    channelProvider: EChannelProvider,
    identity: string,
  ): Promise<IChats> {
    try {
      const email = SolgariApiService.getOwnEmail();
      const urlInstance = new URL(
        `${SolgariApiService.solgariHub}/api/v1/public/chat/${email}/search`,
      );
      urlInstance.searchParams.append('ChannelProvider', channelProvider);
      // TODO: Started doesn't work by some reason
      urlInstance.searchParams.append('State', 'Started');
      urlInstance.searchParams.append('ExternalCustomerIdentity', identity);
      const uri = urlInstance.toString();

      const result = await hub.get(uri);

      return result.data;
    } catch (error) {
      logger.error('[Api] Unable to get chats by identity', error);
    }
  }

  public static async sendNewMessage(chatId: string, text: string) {
    try {
      const encodedText = encodeURIComponent(text);
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/chat/${chatId}/messages?text=${encodedText}`;
      const result = await hub.post(uri);

      return result.data;
    } catch (error) {
      logger.error('[Api] Unable to send new message', error);
    }
  }

  public static async createNewChat(
    identity: string,
    provider: EChannelProvider,
  ) {
    try {
      if (!identity.startsWith('+')) {
        identity = `+${identity}`;
      }
      const email = SolgariApiService.getOwnEmail();
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/chat/${email}`;
      const headers = {
        'Content-Type': 'application/json',
      };
      const body = {
        channelProvider: provider,
        externalClientIdentity: identity,
      };
      const result = await hub.post(uri, body, { headers });
      return result.data;
    } catch (error) {
      logger.error('[Api] Unable to create new chat', error);
    }
  }

  public static async putUploadFile(chatId: string, fileToUpload: any) {
    try {
      const formData = new FormData();
      formData.set('attachment', fileToUpload);
      const response = await hub.post(
        `${SolgariApiService.solgariHub}/api/v2/public/chat/${chatId}/messages`,
        formData,
      );
      return response.data;
    } catch (error) {
      logger.error('[Api] Error uploading file:', error);
      throw error;
    }
  }

  public static async downloadFile(chatId: string, msg: IChatMessage) {
    try {
      const response = await hub.get(
        `${SolgariApiService.solgariHub}/api/v2/public/chat/${chatId}/messages/${msg.messageId}/file`,
        { responseType: 'blob' },
      );
      if (response.data instanceof Blob) {
        const { name, extension } = msg.attachmentMessage!;
        const fileName =
          msg.messageMode === 'Inbound'
            ? `Attachment.${extension}`
            : `${name}.${extension}`;
        const url = URL.createObjectURL(response.data);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      logger.error('[Api] Failed to download file:', error);
      throw error;
    }
  }

  public static async getImage(
    chatId: string,
    msg: IChatMessage,
  ): Promise<Blob> {
    try {
      const response = await hub.get(
        `${SolgariApiService.solgariHub}/api/v2/public/chat/${chatId}/messages/${msg.messageId}/file`,
        { responseType: 'blob' },
      );
      return response.data;
    } catch (error) {
      logger.error('[Api] Failed to download file:', error);
      throw error;
    }
  }

  public static async transferChat(agentEmail: string, chatId: string) {
    const uri = `${SolgariApiService.solgariHub}/api/v1/public/chat/${chatId}/transfer`;
    await hub.post(uri, { toAgentId: agentEmail });
  }

  public static async getVoicemailList(): Promise<IVoicemailsResponse> {
    try {
      return null;
    } catch (error) {
      logger.error('[Api] Error getting voicemail list:', error);
    }
  }

  public static async endConversation(chatId: string) {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/chat/${chatId}/finish`;

      const result = await hub.post(uri);
      return result.data;
    } catch (error) {
      logger.error('[Api] Error ending conversation:', error);
    }
  }

  public static async closeConversation(chatId: string) {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/chat/${chatId}/stop`;
      const result = await hub.post(uri);

      return result.data;
    } catch (error) {
      logger.error('[Api] Error closing conversation:', error);
    }
  }

  public static async getRecordingUri(
    sessionId: string,
    _tenantId: string,
  ): Promise<string> {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/cdr/getrecordingfile/me/${sessionId}`;

      const result = await hub.get<string>(uri, { responseType: 'blob' });
      const audioUrl = window.URL.createObjectURL(new Blob([result.data]));

      return audioUrl;
    } catch (error) {
      logger.error('[Api] Error getting recording uri:', error);
    }
  }

  public static getOwnEmail(): string {
    const tokenData = SolgariApiService.getTokenData();
    const email = tokenData?.preferred_username || tokenData?.unique_name;

    if (!email) {
      logger.error('[Api] There is no email');
      return;
    }

    return email as string;
  }

  public static connectToSignalR(): void {
    const checkUnauthorized = (error: unknown): void => {
      const errorString = error?.toString().toLowerCase() || '';
      const isUnauthorized =
        errorString.includes('401') && errorString.includes('authorization');

      if (isUnauthorized) {
        const isLogged = Auth.isLogged();
        logger.error(
          `[SignalR]: Token ${isLogged ? 'valid but unauthorized' : 'expired'}`,
        );
        SolgariApiService.signalR.stop();
      }
    };

    const start = async () => {
      try {
        const currentState = SolgariApiService.signalR.state;
        if (currentState !== 'Disconnected') {
          return;
        }
        await SolgariApiService.signalR.start();
        logger.log('[SignalR] SignalR connected');
      } catch (error: unknown) {
        logger.critical('[SignalR] SignalR connection error', error);
        checkUnauthorized(error);
      }
    };

    const url = new URL(ENV.SOLGARI_SIGNALR);
    const strUrl = url.toString();

    SolgariApiService.signalR = new HubConnectionBuilder()
      .withUrl(strUrl, {
        accessTokenFactory: async () => {
          const token = Auth.getDynJwtToken();
          return Promise.resolve(token);
        },
      })
      .withAutomaticReconnect([0, 0, 10000])
      .configureLogging(LogLevel.Error)
      .build();

    const connect = (skipReload: boolean) =>
      SolgariApiService.signalR.start().catch((error: unknown) => {
        logger.critical('[SignalR] Connection error ', error);

        checkUnauthorized(error);

        if (skipReload) {
          openNotification({
            onClick: () => connect(true),
            onClose: undefined,
            buttonText: 'Reconnect',
            type: 'error',
            title: "Couldn't connect to signalR",
            text: 'Please try to reconnect',
          });
          return;
        }

        connect(true);
      });

    connect(false);

    SolgariApiService.signalR.on('incomingMessage', (msg) => {
      logger.log('[SignalR] SignalR message income: ', msg);

      BusEvent.sendEvent({
        event: EBusEvent.NEW_CHAT_MESSAGE,
        payload: msg,
      });
    });

    SolgariApiService.signalR.onreconnecting((err) => {
      logger.warn('[SignalR] Attempting to reconnect...', err);
      if (err) {
        checkUnauthorized(err);
      }
    });

    SolgariApiService.signalR.onclose(async (err) => {
      logger.warn('[SignalR] Connection closed', err);
      if (err) {
        checkUnauthorized(err);
      }
      if (!this.forceClose) {
        await start();
      }
    });
  }

  public static onStop(): void {
    SolgariApiService.signalR.stop();
    this.forceClose = true;
  }

  public static async getCallSessionTranscript(
    sessionId: string,
  ): Promise<ITranscriptAnalyticsApi | null> {
    try {
      if (!sessionId) {
        return null;
      }

      const uri = `${SolgariApiService.solgariHub}/api/v1/public/users/me/sessions/${sessionId}/analytics`;
      const response = await hub.get<ITranscriptAnalyticsApi>(uri);

      return response.data;
    } catch (err) {
      logger.error('[Api] Get call session Transcript response failed', err);
      return null;
    }
  }

  public static async setSolgariPresence(presence: EPresenceStatusApi) {
    try {
      const email = SolgariApiService.getOwnEmail();

      if (!presence || !email) {
        return null;
      }

      const uri = `${SolgariApiService.solgariHub}/api/v1/public/presence/setpresence`;
      const headers = {
        'Content-Type': 'application/json',
      };
      const body: ISolgariPresenceApi = {
        email,
        source: 'Teams',
        presence,
      };
      const response = await hub.post(uri, body, { headers });
      return response.data;
    } catch (err) {
      logger.error('[Api] Set api presence failed', err);
      return null;
    }
  }

  public static generateRandomNumberString() {
    return String(
      Math.floor(Math.random() * (999999999999 - 100000000000) + 100000000000),
    );
  }

  public static async createConference(
    conferenceExtension: string,
  ): Promise<string> {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/users/me/conference`;
      const body = {
        subject: SolgariApiService.generateRandomNumberString(),
        mode: 'AUDIO',
        extensionNumber: conferenceExtension,
        maxParticipants: '100',
      };
      const result = await proxy.post(uri, body, {
        responseType: 'json',
        transformResponse: (body) => body,
      });
      return result.data;
    } catch (error) {
      logger.error('[Api] Create conference failed', error);
    }
  }

  public static async deleteConference(conferenceId: string) {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/users/me/conference/${conferenceId}`;
      const result = await proxy.delete(uri);
      return result.data;
    } catch (error) {
      logger.error('[Api] Delete conference failed', error);
    }
  }

  public static async getConferenceParticipants(conferenceId: string) {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/users/me/conference/${conferenceId}/participants`;
      const result = await proxy.get(uri);
      return result;
    } catch (error) {
      logger.error('[Api] Get conference participants failed', error);
    }
  }

  public static async getTenantInfo(): Promise<TenantInfo> {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/tenants/my`;
      const result = await proxy.get<TenantInfo>(uri);
      return result.data;
    } catch (error) {
      logger.critical('[Api] Get Tenant Info error', error);
    }
  }

  public static async getFeatureFlags(
    _tenantId: number,
  ): Promise<FeatureFlags[]> {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/featuretoggle/me`;
      const result = await proxy.get<FeatureFlags[]>(uri);
      return result.data;
    } catch (error) {
      logger.error('[Api] Get Tenant Info error', error);
    }
  }

  public static async updateConferenceParticipants(
    conferenceId: string,
    participantEmails: string[],
    op: string,
  ) {
    try {
      const uri = `${SolgariApiService.solgariHub}/api/v1/public/users/me/conference/${conferenceId}/participants`;
      const result: Record<string, any> = {};
      for (const email of participantEmails) {
        result[email] =
          await SolgariApiService.addOrRemoveConferenceParticipant(
            uri,
            email,
            op,
          );
      }
      return result;
    } catch (error) {
      logger.error('[Api] Update conference participants failed', error);
    }
  }

  public static async getOutboundClids() {
    try {
      const result = await proxy.get<IOutboundClid>(
        `${SolgariApiService.solgariHub}/api/v1/public/clidmanipulation/user/me`,
      );
      return result.data;
    } catch (error) {
      logger.error('[Api] Get outbound CLIDs failed', error);
    }
  }

  public static async setOutboundClids(selectedClid: IClid) {
    const queryString = SolgariApiService.buildQuery({ isActive: true });
    try {
      const result = await proxy.patch(
        `${SolgariApiService.solgariHub}/api/v1/public/clidmanipulation/user/me/clids/${selectedClid.clidId}${queryString}`,
      );
      return result.data;
    } catch (error) {
      logger.error('[Api] Set outbound CLIDs failed', error);
    }
  }

  private static async addOrRemoveConferenceParticipant(
    uri: string,
    participantEmail: string,
    op: string,
  ) {
    try {
      const body = {
        participant: participantEmail,
        op,
      };
      const result = await proxy.post(uri, body);
      return result;
    } catch (error) {
      logger.error('[Api] Add or remove conference participant failed', error);
    }
  }

  private static getTokenData(): Record<string, unknown> {
    try {
      const token = Auth.getDynJwtToken();
      const jwtString =
        typeof token === 'string'
          ? token
          : typeof (token as any)?.accessToken === 'string'
            ? (token as any).accessToken
            : typeof (token as any)?.idToken === 'string'
              ? (token as any).idToken
              : undefined;

      if (!jwtString) {
        logger.critical('JWT token is not a string or missing');
        return {} as Record<string, unknown>;
      }

      const tokenObj = jose.decodeJwt(jwtString);
      return tokenObj as Record<string, unknown>;
    } catch (error) {
      logger.critical('Failed to decode JWT', error);
      return {} as Record<string, unknown>;
    }
  }

  // TODO: rework this method
  private static buildQuery = (queryObj = {}) => {
    const query = Object.entries(queryObj)
      .filter(([, value = '']) =>
        Array.isArray(value)
          ? !!value.length
          : !!(value !== void 0 && value !== null ? value : '')
              .toString()
              .trim().length,
      )
      .map(([key, value]) => {
        const build = (value: any) =>
          `${key}=${window.encodeURIComponent(value)}`;
        return Array.isArray(value) ? value.map(build).join('&') : build(value);
      })
      .join('&');

    return query ? `?${query}` : '';
  };
}
